import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Sun, Moon, Home, FileText, LogOut, Menu, Users, Car, 
  ChevronLeft, ChevronRight, PanelLeftClose, PanelLeft, Settings, Building2, Truck, Globe, CalendarDays, MessageSquare, Calculator
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GarageLayout = () => {
  const { isDarkMode, toggleTheme, theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [garageProfile, setGarageProfile] = useState(null);
  
  // Check if user is logged in
  const isLoggedIn = !!localStorage.getItem('garage_token');
  
  // Check if on login page
  const isLoginPage = location.pathname === '/garage/login' || location.pathname === '/garage';

  // Fetch garage profile for logo
  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem('garage_token');
      if (!token) return;
      
      try {
        const res = await fetch(`${API_URL}/api/garage/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          setGarageProfile(data.garage);
        }
      } catch (error) {
        console.error('Failed to fetch garage profile');
      }
    };
    
    if (isLoggedIn && !isLoginPage) {
      fetchProfile();
    }
  }, [isLoggedIn, isLoginPage]);

  const handleLogout = () => {
    localStorage.removeItem('garage_token');
    localStorage.removeItem('garage_profile');
    navigate('/garage/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Navigation items
  const navItems = [
    { path: '/garage/dashboard', label: 'Dashboard', icon: Home },
    { path: '/garage/bookings', label: 'Bookings', icon: CalendarDays },
    { path: '/garage/jobs', label: 'Jobs', icon: FileText },
    { path: '/garage/customers', label: 'Customers', icon: Users },
    { path: '/garage/vehicles', label: 'Vehicles', icon: Car },
    { path: '/garage/drivers', label: 'Drivers', icon: Truck },
    { path: '/garage/feedback', label: 'Feedback', icon: MessageSquare },
    { path: '/garage/accounting', label: 'Accounting', icon: Calculator },
    { path: '/garage/booking-page', label: 'Booking Page', icon: Globe },
    { path: '/garage/profile', label: 'Settings', icon: Settings },
  ];

  // Show only login page without sidebar
  if (isLoginPage || !isLoggedIn) {
    return <Outlet />;
  }

  return (
    <div className={`min-h-screen ${theme.pageBg} transition-colors duration-200 flex overflow-x-hidden`}>
      {/* Left Sidebar - Desktop */}
      <aside 
        className={`hidden md:flex flex-col fixed left-0 top-0 h-screen ${theme.headerBg} border-r ${theme.border} transition-all duration-300 z-40 ${
          sidebarOpen ? 'w-56' : 'w-16'
        }`}
      >
        {/* Pitcrew Logo */}
        <div 
          className={`flex items-center gap-3 p-4 cursor-pointer hover:opacity-80 transition-opacity border-b ${theme.border}`}
          onClick={() => navigate('/garage/dashboard')}
          data-testid="pitcrew-logo-home"
        >
          <img 
            src="/pitcrew-logo.png" 
            alt="Pitcrew Logo" 
            className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
          />
          {sidebarOpen && (
            <div className="overflow-hidden">
              <h1 className="text-lg font-bold text-white whitespace-nowrap">Pitcrew</h1>
              <p className={`text-xs ${theme.textMuted} whitespace-nowrap`}>powered by Propitstop</p>
            </div>
          )}
        </div>

        {/* Garage Logo & Name - Prominent Display */}
        <div className={`p-3 border-b ${theme.border}`}>
          <div className={`flex ${sidebarOpen ? 'flex-row items-center gap-3' : 'flex-col items-center'} p-2 rounded-xl bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20`}>
            {garageProfile?.logo_url ? (
              <img 
                src={`${API_URL}/api${garageProfile.logo_url}`}
                alt={garageProfile?.name || 'Garage Logo'}
                className={`${sidebarOpen ? 'w-12 h-12' : 'w-10 h-10'} rounded-lg object-cover border-2 border-orange-500/30 flex-shrink-0 bg-slate-800`}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className={`${sidebarOpen ? 'w-12 h-12' : 'w-10 h-10'} rounded-lg bg-gradient-to-br from-orange-500 to-red-600 items-center justify-center flex-shrink-0 ${garageProfile?.logo_url ? 'hidden' : 'flex'}`}
            >
              <Building2 className="w-6 h-6 text-white" />
            </div>
            {sidebarOpen && (
              <div className="overflow-hidden flex-1 min-w-0">
                <p className="text-sm font-semibold text-orange-400 truncate">
                  {garageProfile?.name || 'Your Garage'}
                </p>
                <p className={`text-xs ${theme.textMuted} truncate`}>
                  {garageProfile?.city || 'Garage Owner'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-2 space-y-1">
          {navItems.map((item) => (
            <Button
              key={item.path}
              variant="ghost"
              onClick={() => navigate(item.path)}
              className={`w-full ${sidebarOpen ? 'justify-start' : 'justify-center'} ${
                isActive(item.path) 
                  ? 'bg-orange-500/20 text-orange-500 hover:bg-orange-500/30' 
                  : `${theme.textSecondary} ${theme.hoverBg}`
              }`}
              title={!sidebarOpen ? item.label : undefined}
            >
              <item.icon className={`w-5 h-5 ${sidebarOpen ? 'mr-3' : ''}`} />
              {sidebarOpen && <span>{item.label}</span>}
            </Button>
          ))}
        </nav>

        {/* Bottom Actions */}
        <div className={`p-2 border-t ${theme.border} space-y-1`}>
          {/* Theme Toggle */}
          <Button
            variant="ghost"
            onClick={toggleTheme}
            className={`w-full ${sidebarOpen ? 'justify-start' : 'justify-center'} ${theme.textSecondary} ${theme.hoverBg}`}
            title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDarkMode ? (
              <Sun className={`w-5 h-5 text-yellow-400 ${sidebarOpen ? 'mr-3' : ''}`} />
            ) : (
              <Moon className={`w-5 h-5 text-slate-400 ${sidebarOpen ? 'mr-3' : ''}`} />
            )}
            {sidebarOpen && <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>}
          </Button>

          {/* Logout */}
          <Button
            variant="ghost"
            onClick={handleLogout}
            className={`w-full ${sidebarOpen ? 'justify-start' : 'justify-center'} text-red-400 hover:text-red-300 hover:bg-red-500/10`}
            title="Logout"
          >
            <LogOut className={`w-5 h-5 ${sidebarOpen ? 'mr-3' : ''}`} />
            {sidebarOpen && <span>Logout</span>}
          </Button>

          {/* Collapse Toggle */}
          <Button
            variant="ghost"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`w-full ${sidebarOpen ? 'justify-start' : 'justify-center'} ${theme.textMuted} ${theme.hoverBg}`}
            title={sidebarOpen ? 'Collapse Sidebar' : 'Expand Sidebar'}
          >
            {sidebarOpen ? (
              <>
                <PanelLeftClose className="w-5 h-5 mr-3" />
                <span>Collapse</span>
              </>
            ) : (
              <PanelLeft className="w-5 h-5" />
            )}
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className={`md:hidden fixed top-0 left-0 right-0 z-50 ${theme.headerBg} border-b ${theme.border}`}>
        <div className="flex items-center justify-between h-14 px-4">
          <div 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/garage/dashboard')}
          >
            {/* Show garage logo on mobile if available, otherwise Pitcrew logo */}
            {garageProfile?.logo_url ? (
              <img 
                src={`${API_URL}/api${garageProfile.logo_url}`}
                alt={garageProfile?.name || 'Garage Logo'}
                className="w-8 h-8 rounded-lg object-cover border border-orange-500/30 bg-slate-800"
                onError={(e) => {
                  e.target.src = '/pitcrew-logo.png';
                }}
              />
            ) : (
              <img 
                src="/pitcrew-logo.png" 
                alt="Pitcrew Logo" 
                className="w-8 h-8 rounded-lg object-cover"
              />
            )}
            <span className="text-white font-bold text-sm truncate max-w-[120px]">
              {garageProfile?.name || 'Pitcrew'}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className={theme.textSecondary}
            >
              {isDarkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={theme.textSecondary}
            >
              <Menu className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className={`${theme.cardBgSolid} border-t ${theme.border} py-2 px-3`}>
            {/* Garage Info Card */}
            <div className="flex items-center gap-3 p-2 mb-2 rounded-lg bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20">
              {garageProfile?.logo_url ? (
                <img 
                  src={`${API_URL}/api${garageProfile.logo_url}`}
                  alt={garageProfile?.name || 'Garage Logo'}
                  className="w-10 h-10 rounded-lg object-cover border border-orange-500/30 bg-slate-800"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-orange-400 truncate">
                  {garageProfile?.name || 'Your Garage'}
                </p>
                <p className={`text-xs ${theme.textMuted} truncate`}>
                  {garageProfile?.city || 'Powered by Pitcrew'}
                </p>
              </div>
            </div>
            
            {navItems.map((item) => (
              <Button
                key={item.path}
                variant="ghost"
                onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                className={`w-full justify-start ${
                  isActive(item.path) ? 'bg-orange-500/20 text-orange-500' : theme.textSecondary
                }`}
              >
                <item.icon className="w-4 h-4 mr-3" />
                {item.label}
              </Button>
            ))}
            <hr className={`my-2 ${theme.border}`} />
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start text-red-400"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Logout
            </Button>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main 
        className={`flex-1 transition-all duration-300 pt-14 md:pt-0 overflow-x-hidden w-full ${
          sidebarOpen ? 'md:ml-56' : 'md:ml-16'
        }`}
      >
        <Outlet />
      </main>
    </div>
  );
};

export default GarageLayout;
